import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useGetFirebaseStore } from "../../service/hooks/useGetFirebaseStore";
import heroPlaceholder from "../../assets/new/Picsart_25-04-09_17-27-39-920.jpg";
import paradoxPoster from "../../assets/new/paradox effect.jpg";
import magazinePoster from "../../assets/new/SUPERGIRL.jpeg";
import { WORK_CATEGORIES, workPath, landingStripItems } from "../../utils/workRoutes";
import { SiteHeader } from "./SiteHeader";
import { usePageScroll } from "./usePageScroll";
import "./LandingGrid.css";

const canLoadImage = (src) =>
  Boolean(src) &&
  !/firebasestorage\.googleapis\.com|appspot\.com/i.test(src);

const workImageKey = (item, category) =>
  `${category || item?.category || ""}:${item?.id}`;

function takeUniqueWorks(list, used, count, category) {
  const picked = [];
  for (const item of list || []) {
    if (!item || !canLoadImage(item.ImageUrl)) continue;
    const key = workImageKey(item, category);
    if (!item.id || used.has(key)) continue;
    used.add(key);
    picked.push(category ? { ...item, category } : item);
    if (picked.length >= count) break;
  }
  return picked;
}

function categoryPreviews(list, category, used, limit = 3) {
  const tagged = (list || []).map((item) => ({ ...item, category }));
  const unique = takeUniqueWorks(tagged, used, limit);
  if (unique.length >= limit) return unique;
  const fill = new Set(used);
  unique.forEach((item) => fill.delete(workImageKey(item)));
  return unique.concat(takeUniqueWorks(tagged, fill, limit - unique.length));
}

const Cross = ({ className }) => (
  <span className={`lg-cross ${className || ""}`} aria-hidden="true">
    +
  </span>
);

const WorksMarquee = ({ items }) => {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  const track = useMemo(() => {
    if (!items.length) return [];
    return [...items, ...items];
  }, [items]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const trackEl = trackRef.current;
    if (!wrap || !trackEl || !items.length) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let offset = 0;
    let paused = false;
    let dragging = false;
    let lastX = 0;
    let moved = 0;
    let pointerId = null;
    let raf = 0;
    let lastTs = 0;
    let clickGuardTimer = 0;

    const loopWidth = () => trackEl.scrollWidth / 2;

    const apply = () => {
      const loop = loopWidth();
      if (loop > 0) {
        offset = ((offset % loop) + loop) % loop;
      }
      trackEl.style.transform = `translate3d(${-offset}px, 0, 0)`;
    };

    const durationMs = () => {
      if (window.innerWidth < 600) return 90000;
      if (window.innerWidth < 924) return 75000;
      if (window.innerWidth >= 1440) return 70000;
      return 60000;
    };

    const tick = (ts) => {
      if (!lastTs) lastTs = ts;
      const dt = Math.min(48, ts - lastTs);
      lastTs = ts;

      if (!paused && !dragging && !reduceMotion) {
        const loop = loopWidth();
        if (loop > 0) {
          offset += (loop / durationMs()) * dt;
        }
        apply();
      }

      raf = requestAnimationFrame(tick);
    };

    const onWheel = (event) => {
      const dx =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (!dx) return;
      event.preventDefault();
      paused = true;
      offset += dx;
      apply();
    };

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      dragging = true;
      paused = true;
      lastX = event.clientX;
      moved = 0;
      pointerId = event.pointerId;
    };

    const onPointerMove = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = lastX - event.clientX;
      lastX = event.clientX;
      if (!dx) return;
      moved += Math.abs(dx);
      offset += dx;
      apply();
      if (moved > 6) {
        if (!wrap.hasPointerCapture?.(event.pointerId)) {
          wrap.setPointerCapture?.(event.pointerId);
          wrap.classList.add("is-dragging");
        }
        event.preventDefault();
      }
    };

    const endDrag = (event) => {
      if (!dragging) return;
      if (event && pointerId != null && event.pointerId !== pointerId) return;
      dragging = false;
      pointerId = null;
      wrap.classList.remove("is-dragging");
      if (moved > 8) {
        wrap.dataset.dragged = "1";
        clearTimeout(clickGuardTimer);
        clickGuardTimer = window.setTimeout(() => {
          delete wrap.dataset.dragged;
        }, 80);
      }
      const keepPaused =
        event?.pointerType === "mouse" && wrap.matches(":hover");
      paused = keepPaused;
    };

    const onClickCapture = (event) => {
      if (wrap.dataset.dragged !== "1") return;
      event.preventDefault();
      event.stopPropagation();
      delete wrap.dataset.dragged;
    };

    const onEnter = () => {
      paused = true;
    };

    const onLeave = () => {
      if (!dragging) paused = false;
    };

    wrap.addEventListener("wheel", onWheel, { passive: false });
    wrap.addEventListener("pointerdown", onPointerDown);
    wrap.addEventListener("pointermove", onPointerMove, { passive: false });
    wrap.addEventListener("pointerup", endDrag);
    wrap.addEventListener("pointercancel", endDrag);
    wrap.addEventListener("click", onClickCapture, true);
    wrap.addEventListener("mouseenter", onEnter);
    wrap.addEventListener("mouseleave", onLeave);

    apply();
    raf = requestAnimationFrame(tick);

    const ro = new ResizeObserver(apply);
    ro.observe(trackEl);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(clickGuardTimer);
      ro.disconnect();
      wrap.classList.remove("is-dragging");
      wrap.removeEventListener("wheel", onWheel);
      wrap.removeEventListener("pointerdown", onPointerDown);
      wrap.removeEventListener("pointermove", onPointerMove);
      wrap.removeEventListener("pointerup", endDrag);
      wrap.removeEventListener("pointercancel", endDrag);
      wrap.removeEventListener("click", onClickCapture, true);
      wrap.removeEventListener("mouseenter", onEnter);
      wrap.removeEventListener("mouseleave", onLeave);
    };
  }, [items]);

  if (!items.length) {
    return <div className="lg-marquee lg-marquee--empty" />;
  }

  return (
    <div
      ref={wrapRef}
      className="lg-marquee"
      aria-label="Selected works"
    >
      <div ref={trackRef} className="lg-marquee-track">
        {track.map((item, index) => {
          const src = item.ImageUrl || "";
          const hasImage = canLoadImage(src);

          return (
            <Link
              to={workPath(item.category, item.id)}
              key={`${item.id}-${index}`}
              className={`lg-card${hasImage ? "" : " lg-card--noimg"}`}
              title={item.title || "Work"}
              draggable={false}
            >
              {hasImage && (
                <img
                  className="lg-card-image"
                  src={src}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement?.classList.add("lg-card--noimg");
                  }}
                />
              )}
              <span className="lg-card-caption">
                <span className="lg-card-caption-title">
                  {item.title || "Untitled"}
                </span>
                {item.description ? (
                  <span className="lg-card-caption-desc">{item.description}</span>
                ) : null}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

function useFitDisplay(refs) {
  useLayoutEffect(() => {
    const fit = () => {
      const mobile = window.matchMedia("(max-width: 924px)").matches;

      refs.forEach((ref) => {
        const el = ref.current;
        if (!el) return;
        el.style.fontSize = "";

        // On mobile let CSS drive size; only shrink if a line overflows.
        if (mobile) {
          const elStyle = getComputedStyle(el);
          const elPadL = parseFloat(elStyle.paddingLeft) || 0;
          const elPadR = parseFloat(elStyle.paddingRight) || 0;
          const maxWidth = Math.max(40, el.clientWidth - elPadL - elPadR);
          const spans = [...el.querySelectorAll(":scope > span")];
          let size = parseFloat(elStyle.fontSize);
          if (!Number.isFinite(size) || size <= 0) return;

          const contentWidth = () =>
            Math.max(0, ...spans.map((s) => s.scrollWidth));

          let guard = 0;
          while (contentWidth() > maxWidth + 1 && size > 18 && guard < 64) {
            size *= 0.97;
            el.style.fontSize = `${size}px`;
            guard += 1;
          }
          return;
        }

        const parent = el.parentElement;
        if (!parent) return;

        const elStyle = getComputedStyle(el);
        const elPadL = parseFloat(elStyle.paddingLeft) || 0;
        const elPadR = parseFloat(elStyle.paddingRight) || 0;
        const elPadT = parseFloat(elStyle.paddingTop) || 0;
        const elPadB = parseFloat(elStyle.paddingBottom) || 0;

        const maxWidth = Math.max(40, el.clientWidth - elPadL - elPadR);
        const maxHeight = Math.max(40, el.clientHeight - elPadT - elPadB);
        if (!maxWidth || !maxHeight) return;

        const spans = [...el.querySelectorAll(":scope > span")];
        const lines = Math.max(1, spans.length);
        const ratio = 0.84;

        const fromCss = parseFloat(getComputedStyle(el).fontSize);
        const fromHeight = maxHeight / (lines * ratio);
        let size = Math.max(fromCss || 0, fromHeight || 0);
        if (!Number.isFinite(size) || size <= 0) return;

        el.style.fontSize = `${size}px`;

        const contentWidth = () =>
          Math.max(0, ...spans.map((s) => s.scrollWidth));
        const contentHeight = () => lines * size * ratio;

        let guard = 0;
        while (
          (contentWidth() > maxWidth + 1 || contentHeight() > maxHeight + 2) &&
          size > 18 &&
          guard < 64
        ) {
          size *= 0.96;
          el.style.fontSize = `${size}px`;
          guard += 1;
        }
      });
    };

    fit();
    const ro = new ResizeObserver(fit);
    refs.forEach((ref) => {
      if (ref.current) ro.observe(ref.current);
      if (ref.current?.parentElement) ro.observe(ref.current.parentElement);
    });
    window.addEventListener("resize", fit);
    document.fonts?.ready?.then(fit);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [refs]);
}

function useFitVerticalText(ref) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      const style = getComputedStyle(el);
      const padT = parseFloat(style.paddingTop) || 0;
      const padB = parseFloat(style.paddingBottom) || 0;
      const target = Math.max(40, el.clientHeight - padT - padB);
      if (!target) return;

      const measure = () => {
        const range = document.createRange();
        range.selectNodeContents(el);
        const rect = range.getBoundingClientRect();
        // After rotate(180deg), the text run is still the larger axis of the range box.
        return Math.max(rect.width, rect.height);
      };

      el.style.letterSpacing = "0.06em";

      if (measure() >= target) {
        let lo = 0.01;
        let hi = 0.06;
        for (let i = 0; i < 16; i++) {
          const mid = (lo + hi) / 2;
          el.style.letterSpacing = `${mid}em`;
          if (measure() > target) hi = mid;
          else lo = mid;
        }
        el.style.letterSpacing = `${lo}em`;
        return;
      }

      let lo = 0.06;
      let hi = 2.4;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        el.style.letterSpacing = `${mid}em`;
        if (measure() > target) hi = mid;
        else lo = mid;
      }
      el.style.letterSpacing = `${lo}em`;
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener("resize", fit);
    document.fonts?.ready?.then(fit);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [ref]);
}

function MissionSection() {
  return (
    <section className="lg-mission" data-scene="mission">
      <div className="lg-mission-inner">
        <p className="lg-mission-overline">01 — Practice</p>
        <p className="lg-mission-copy">
          Composer and musician for film, documentary, brand and stage.
          Original scores, sound design and live performance — each project
          is cut to the story, the room, and the people in it.
        </p>
        <p className="lg-mission-sub">Tailoring music. Everyday.</p>
      </div>
    </section>
  );
}

function SplitFeature({ category, images = [], href }) {
  if (!category) return null;
  const shots = (images || []).filter(Boolean).slice(0, 3);

  return (
    <section className="lg-split" data-scene="split">
      <div className="lg-split-copy">
        <p className="lg-section-kicker">
          <span>02</span>
          <span className="lg-section-kicker-rule" aria-hidden="true" />
          <span>Featured</span>
        </p>
        <div className="lg-split-copy-main">
          <h2 className="lg-split-title">{category.title}</h2>
          <p className="lg-split-lead">{category.copy}</p>
        </div>
        <Link to={href} className="lg-section-link">
          View works →
        </Link>
      </div>
      <div className="lg-split-asset">
        <div className="lg-split-movel">
          {shots.length ? (
            <div className={`lg-split-stack lg-split-stack--${shots.length}`}>
              {shots.map((src, index) => (
                <img
                  key={`${src}-${index}`}
                  className="lg-split-shot"
                  src={src}
                  alt=""
                />
              ))}
            </div>
          ) : (
            <div className="lg-split-fallback" />
          )}
        </div>
      </div>
    </section>
  );
}

function PassingQuote({ images = [] }) {
  const left = images[0];
  const right = images[1];

  return (
    <section className="lg-quote" data-scene="quote">
      <div className="lg-quote-sticky">
        <div className="lg-quote-img lg-quote-img--a">
          {left ? <img src={left} alt="" /> : <div className="lg-split-fallback" />}
        </div>
        <figure className="lg-quote-copy">
          <blockquote>
            Each project is tailored to the story, the room, and the brand.
          </blockquote>
          <figcaption>Alessandro Giustini</figcaption>
        </figure>
        <div className="lg-quote-img lg-quote-img--b">
          {right ? <img src={right} alt="" /> : <div className="lg-split-fallback" />}
        </div>
      </div>
    </section>
  );
}

function ScrollGallery({ items }) {
  const stickyRef = useRef(null);
  const trackRef = useRef(null);

  useLayoutEffect(() => {
    const sticky = stickyRef.current;
    const track = trackRef.current;
    if (!sticky || !track) return;

    const measure = () => {
      const travel = Math.min(0, sticky.clientWidth - track.scrollWidth - 32);
      sticky.style.setProperty("--travel", `${travel}px`);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    ro.observe(sticky);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items]);

  if (!items.length) return null;

  return (
    <section className="lg-hscroll" data-scene="gallery" aria-label="Selected works">
      <div className="lg-hscroll-sticky" ref={stickyRef}>
        <p className="lg-hscroll-kicker">
          <span>03</span>
          <span className="lg-section-kicker-rule" aria-hidden="true" />
          <span>Selected works</span>
        </p>
        <div className="lg-hscroll-track" ref={trackRef}>
          {items.map((item) => {
            const src = item.ImageUrl || "";
            const hasImage = canLoadImage(src);
            return (
              <Link
                to={workPath(item.category, item.id)}
                key={item.id}
                className={`lg-hscroll-card${hasImage ? "" : " lg-card--noimg"}`}
              >
                {hasImage && <img src={src} alt="" loading="lazy" />}
                <span className="lg-hscroll-caption">
                  <span>{item.title || "Untitled"}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MagazineReveal({ image, href }) {
  return (
    <section className="lg-mag" data-scene="magazine">
      <div className="lg-mag-sticky">
        <div className="lg-mag-image">
          <img src={image || heroPlaceholder} alt="" />
        </div>
        <div className="lg-mag-copy">
          <p className="lg-section-kicker">
            <span>04</span>
            <span className="lg-section-kicker-rule" aria-hidden="true" />
            <span>The experience</span>
          </p>
          <h2>Time to put sound in the room.</h2>
          <p>
            Whether it is a film score, a live set or a brand world, the work is
            built to hold the picture — and the people watching it.
          </p>
          <Link to={href} className="lg-section-link lg-section-link--accent">
            Let's talk
          </Link>
        </div>
      </div>
    </section>
  );
}

function SelectedSection({ worksByCategory, excludeImageKeys = [] }) {
  return (
    <section className="lg-section" id="selected" aria-labelledby="lg-section-title" data-scene="practice">
      <div className="lg-section-inner">
        <header className="lg-section-head" data-reveal>
          <p className="lg-section-kicker">
            <span>05</span>
            <span className="lg-section-kicker-rule" aria-hidden="true" />
            <span>SELECTED PRACTICE</span>
          </p>
          <div className="lg-section-titles">
            <h2 id="lg-section-title" className="lg-section-title">
              Tailoring music.
              <br />
              Everyday.
            </h2>
            <p className="lg-section-lead">
              <span>From film scoring to live electronic performance —</span>
              <span>
                each project is tailored to the story, the room, and the brand.
              </span>
            </p>
          </div>
        </header>

        <div className="lg-discipline-list">
          {WORK_CATEGORIES.map((item) => {
            const preview = categoryPreviews(
              worksByCategory[item.key],
              item.key,
              new Set(excludeImageKeys)
            );
            return (
              <div key={item.key} className="lg-discipline" data-reveal>
                <Link
                  to={`/works?category=${item.key}`}
                  className="lg-discipline-meta"
                >
                  <span className="lg-discipline-index">{item.index}</span>
                  <h3 className="lg-discipline-title">{item.title}</h3>
                  <p className="lg-discipline-copy">{item.copy}</p>
                </Link>

                <div className="lg-discipline-previews">
                  {preview.map((work) => (
                    <Link
                      key={work.id}
                      to={workPath(item.key, work.id)}
                      className="lg-discipline-thumb"
                      style={{
                        backgroundImage: work.ImageUrl
                          ? `url(${work.ImageUrl})`
                          : undefined,
                      }}
                    >
                      <span className="lg-card-caption">
                        <span className="lg-card-caption-title">
                          {work.title || "Untitled"}
                        </span>
                        {work.description ? (
                          <span className="lg-card-caption-desc">
                            {work.description}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  ))}
                  {!preview.length && (
                    <>
                      <div className="lg-discipline-thumb" />
                      <div className="lg-discipline-thumb" />
                      <div className="lg-discipline-thumb" />
                    </>
                  )}
                </div>

                <Link
                  to={`/works?category=${item.key}`}
                  className="lg-discipline-cta"
                >
                  View works →
                </Link>
              </div>
            );
          })}
        </div>

        <div className="lg-section-footer">
          <Link to="/works" className="lg-section-link">
            All works
          </Link>
          <Link to="/about" className="lg-section-link">
            Who I am
          </Link>
          <Link to="/contact" className="lg-section-link lg-section-link--accent">
            Let's talk
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function LandingGrid() {
  const year = new Date().getFullYear();
  const pageRef = useRef(null);
  const titleRef = useRef(null);
  const subRef = useRef(null);
  const verticalRef = useRef(null);
  useFitDisplay(useMemo(() => [titleRef, subRef], []));
  useFitVerticalText(verticalRef);
  usePageScroll(pageRef);

  const { data: documentary = [] } = useGetFirebaseStore("documentary");
  const { data: commercial = [] } = useGetFirebaseStore("commercial");
  const { data: press = [] } = useGetFirebaseStore("press");
  const { data: lunatik = [] } = useGetFirebaseStore("lunatik");
  const { data: landingMedia = [] } = useGetFirebaseStore("landing_media");

  const worksByCategory = useMemo(
    () => ({ documentary, commercial, press, lunatik }),
    [documentary, commercial, press, lunatik]
  );

  const works = useMemo(() => {
    const tag = (list, category) =>
      list.map((item) => ({ ...item, category }));
    return landingStripItems([
      ...tag(documentary, "documentary"),
      ...tag(commercial, "commercial"),
      ...tag(press, "press"),
      ...tag(lunatik, "lunatik"),
    ].filter((item) => item?.ImageUrl || item?.title));
  }, [documentary, commercial, press, lunatik]);

  const imageWorks = useMemo(
    () => works.filter((item) => canLoadImage(item.ImageUrl)),
    [works]
  );

  const featured = useMemo(() => {
    for (const category of WORK_CATEGORIES) {
      const list = (worksByCategory[category.key] || []).filter((work) =>
        canLoadImage(work.ImageUrl)
      );
      const images = [];
      const seen = new Set();
      for (const work of [...landingStripItems(list), ...list]) {
        const src = work.ImageUrl;
        if (!src || seen.has(src)) continue;
        seen.add(src);
        images.push(src);
        if (images.length === 3) break;
      }
      if (images.length) {
        return {
          category,
          images,
          href: `/works?category=${category.key}`,
        };
      }
    }
    return {
      category: WORK_CATEGORIES[0],
      images: [heroPlaceholder],
      href: "/works",
    };
  }, [worksByCategory]);

  const leftoverImages = useMemo(() => {
    const used = new Set(featured.images || []);
    return imageWorks
      .filter((item) => {
        if (!canLoadImage(item.ImageUrl) || used.has(item.ImageUrl)) return false;
        if (item.category === "documentary" && String(item.id) === "1") return false;
        return true;
      })
      .map((item) => item.ImageUrl);
  }, [imageWorks, featured.images]);

  const landingById = useMemo(() => {
    const map = {};
    landingMedia.forEach((row) => {
      if (row?.id && canLoadImage(row.ImageUrl)) map[row.id] = row.ImageUrl;
    });
    return map;
  }, [landingMedia]);

  const quoteImages = [
    landingById.quote_a || paradoxPoster,
    landingById.quote_b || leftoverImages[0] || featured.images?.[1],
  ];
  const magazineImage = magazinePoster;

  return (
    <div className="lg-page" ref={pageRef}>
      <div className="lg-page-content">
      <section className="lg-hero-pin" data-scene="hero">
      <section className="lg-root" aria-label="Hero">
        <div className="lg-grid-bg" aria-hidden="true" />

        <SiteHeader />

      <Cross className="lg-cross--a" />
      <Cross className="lg-cross--b" />
      <Cross className="lg-cross--c" />
      <Cross className="lg-cross--d" />
      <Cross className="lg-cross--e" />

      <div className="lg-divider" aria-hidden="true" />

      <p className="lg-vertical" ref={verticalRef}>
        WHETHER YOU NEED ORIGINAL SCORE, SOUND DESIGN, OR LIVE PERFORMANCE —
        TAILORING MUSIC FOR FILM, BRAND AND STAGE
      </p>

        <main className="lg-main">
          <section className="lg-hero-top">
            <div className="lg-hero-media" aria-hidden="true">
              <img src={heroPlaceholder} alt="" />
            </div>
            <h1 className="lg-display" ref={titleRef}>
              <span>ALESSANDRO</span>
              <span>GIUSTINI</span>
            </h1>
          </section>

          <WorksMarquee items={works} />

          <section className="lg-hero-bottom">
            <h2 className="lg-display lg-display--sub" ref={subRef}>
              <span>
                MUSIC <span className="lg-amp">FOR</span>
              </span>
              <span>FILMS, DOC,</span>
              <span>BRANDS</span>
            </h2>
          </section>
        </main>

        <div className="lg-footer lg-footer--hero">
          <span className="lg-footer-rule" aria-hidden="true" />
          <span className="lg-copy">©{year}</span>
        </div>
      </section>
      </section>

      <MissionSection />
      <SplitFeature
        category={featured.category}
        images={featured.images}
        href={featured.href}
      />
      <PassingQuote images={quoteImages} />
      <ScrollGallery items={imageWorks.slice(0, 8)} />
      <MagazineReveal image={magazineImage} href="/contact" />

      <SelectedSection worksByCategory={worksByCategory} />

      <footer className="lg-page-footer">
        <span className="lg-footer-rule" aria-hidden="true" />
        <span className="lg-copy">©{year} Alessandro Giustini</span>
        <a href="mailto:hello@alessandrogiustini.com">hello@alessandrogiustini.com</a>
      </footer>
      </div>
    </div>
  );
}
